import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTranslations } from "next-intl"
import {
	ArrowLeft, Brain, Building2, Calendar, MapPin, FileText, ExternalLink, Tag,
	Download, Shield, Mail, Phone, User, Copy, Check, FileDown, Users,
	Briefcase, Gavel, FileSearch, Cpu, CircleDashed, CheckCircle,
} from "lucide-react"
import { DashboardLayout } from "@/components/common/DashboardLayout"
import { LoadingSpinner } from "@/components/common/LoadingSpinner"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { api } from "@/lib/apiService"
import { useLocaleStore } from "@/stores/localeStore"
import { formatCurrency, formatDate, formatDateRelative, getRelevanceBgColor } from "@/lib/utils"
import type { Tender, TenderDocument, TenderContact } from "@/types/tender.types"

interface TenderDetailResponse {
	success: boolean
	data: Tender
}

interface DocumentsResponse {
	success: boolean
	data: TenderDocument[]
}

interface ContactsResponse {
	success: boolean
	data: TenderContact[]
}

type Tab = "details" | "documents" | "contacts"

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
	return (
		<div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-100">
			<div className="text-[#2D8A5E] mt-0.5 shrink-0">{icon}</div>
			<div className="min-w-0">
				<p className="text-xs text-muted-foreground font-medium mb-0.5">{label}</p>
				<div className="text-sm font-semibold text-gray-800 break-words">{value}</div>
			</div>
		</div>
	)
}

function StatusBadge({ status }: { status: string }) {
	const colorMap: Record<string, string> = {
		Publicada: "bg-green-50 text-green-700 border-green-200",
		Adjudicada: "bg-blue-50 text-blue-700 border-blue-200",
		Resuelta: "bg-gray-50 text-gray-600 border-gray-200",
		Anulada: "bg-red-50 text-red-600 border-red-200",
	}
	const cls = colorMap[status] || "bg-gray-50 text-gray-600 border-gray-200"
	return (
		<span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${cls}`}>
			{status}
		</span>
	)
}

function RelevanceCard({ score, reason }: { score?: number | null; reason?: string | null }) {
	const t = useTranslations("tenderDetail")

	if (score == null) {
		return (
			<div className="bg-gray-50 rounded-xl border border-gray-100 p-5">
				<div className="flex items-center gap-2 mb-2">
					<Brain className="h-5 w-5 text-muted-foreground" />
					<h3 className="font-semibold text-gray-700">{t("aiAnalysis")}</h3>
				</div>
				<p className="text-sm text-muted-foreground">{t("notScored")}</p>
			</div>
		)
	}

	const bgCls = getRelevanceBgColor(score)
	const barWidth = `${score}%`

	return (
		<div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
			<div className="flex items-center gap-2 mb-4">
				<Brain className="h-5 w-5 text-[#2D8A5E]" />
				<h3 className="font-semibold text-gray-800">{t("aiAnalysis")}</h3>
			</div>

			<div className="flex items-center justify-between mb-2">
				<span className="text-sm text-muted-foreground">{t("relevanceScore")}</span>
				<span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold border ${bgCls}`}>
					<Brain className="h-3.5 w-3.5" />
					{score} / 100
				</span>
			</div>

			{/* Progress bar */}
			<div className="h-2 bg-gray-100 rounded-full mb-4 overflow-hidden">
				<div
					className={`h-full rounded-full transition-all ${score >= 75 ? "bg-emerald-500" : score >= 50 ? "bg-amber-500" : "bg-red-400"}`}
					style={{ width: barWidth }}
				/>
			</div>

			{reason && (
				<div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
					<p className="text-xs text-muted-foreground font-medium mb-1">{t("relevanceReason")}</p>
					<p className="text-sm text-gray-700 leading-relaxed">{reason}</p>
				</div>
			)}
		</div>
	)
}

function getMimeIcon(mime: string) {
	if (mime === "application/pdf") return <FileText className="h-5 w-5 text-red-500" />
	if (mime.includes("zip")) return <FileDown className="h-5 w-5 text-amber-500" />
	if (mime.includes("xml")) return <FileText className="h-5 w-5 text-blue-500" />
	return <FileText className="h-5 w-5 text-gray-400" />
}

function formatFileSize(bytes?: number): string {
	if (!bytes) return ""
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`
	return `${(bytes / 1048576).toFixed(1)} MB`
}

function DocumentsTab({ tenderId }: { tenderId: string }) {
	const t = useTranslations("tenderDetail.documents")
	const [documents, setDocuments] = useState<TenderDocument[]>([])
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		api.get<DocumentsResponse>(`/tenders/${tenderId}/documents`)
			.then((res) => setDocuments(res.data))
			.catch(() => {})
			.finally(() => setIsLoading(false))
	}, [tenderId])

	if (isLoading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>

	if (documents.length === 0) {
		return (
			<div className="text-center py-12">
				<FileText className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
				<p className="text-sm text-muted-foreground">{t("empty")}</p>
			</div>
		)
	}

	const pliegos = documents.filter((d) => d.is_pliego)
	const others = documents.filter((d) => !d.is_pliego)

	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground">{t("count", { count: documents.length })}</p>

			{pliegos.length > 0 && (
				<div className="space-y-2">
					<h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
						<Shield className="h-3.5 w-3.5" />
						{t("pliego")}s
					</h4>
					<div className="space-y-1.5">
						{pliegos.map((doc) => (
							<DocumentRow key={doc.id} doc={doc} highlight />
						))}
					</div>
				</div>
			)}

			{others.length > 0 && (
				<div className="space-y-2">
					{pliegos.length > 0 && (
						<h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-4">
							{t("title")}
						</h4>
					)}
					<div className="space-y-1.5">
						{others.map((doc) => (
							<DocumentRow key={doc.id} doc={doc} />
						))}
					</div>
				</div>
			)}
		</div>
	)
}

function DocumentRow({ doc, highlight }: { doc: TenderDocument; highlight?: boolean }) {
	const t = useTranslations("tenderDetail.documents")
	return (
		<a
			href={doc.url}
			target="_blank"
			rel="noopener noreferrer"
			className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors hover:bg-gray-50 group ${
				highlight ? "border-[#2D8A5E]/20 bg-[#2D8A5E]/[0.02]" : "border-gray-100"
			}`}
		>
			{getMimeIcon(doc.mime)}
			<div className="flex-1 min-w-0">
				<p className="text-sm font-medium text-gray-800 truncate">{doc.name}</p>
				<div className="flex items-center gap-2 text-xs text-muted-foreground">
					{doc.mime.split("/")[1]?.toUpperCase()}
					{doc.size ? <span>{formatFileSize(doc.size)}</span> : null}
					{doc.date && <span>{formatDate(doc.date)}</span>}
					{doc.pdf_text_extracted && (
						<span className="text-[#2D8A5E] flex items-center gap-0.5">
							<Check className="h-3 w-3" />
							{t("textExtracted")}
						</span>
					)}
				</div>
			</div>
			{highlight && (
				<Badge variant="outline" className="text-[#2D8A5E] border-[#2D8A5E]/30 text-xs shrink-0">
					{t("pliego")}
				</Badge>
			)}
			<Download className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
		</a>
	)
}

function ContactsTab({ tenderId }: { tenderId: string }) {
	const t = useTranslations("tenderDetail.contacts")
	const [contacts, setContacts] = useState<TenderContact[]>([])
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		api.get<ContactsResponse>(`/tenders/${tenderId}/contacts`)
			.then((res) => setContacts(res.data))
			.catch(() => {})
			.finally(() => setIsLoading(false))
	}, [tenderId])

	if (isLoading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>

	if (contacts.length === 0) {
		return (
			<div className="text-center py-12">
				<Users className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
				<p className="text-sm text-muted-foreground">{t("empty")}</p>
			</div>
		)
	}

	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground">{t("count", { count: contacts.length })}</p>
			<div className="space-y-3">
				{contacts.map((contact) => (
					<ContactCard key={contact.id} contact={contact} />
				))}
			</div>
		</div>
	)
}

function CopyButton({ text }: { text: string }) {
	const t = useTranslations("tenderDetail.contacts")
	const [copied, setCopied] = useState(false)

	const handleCopy = (e: React.MouseEvent) => {
		e.stopPropagation()
		navigator.clipboard.writeText(text)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	return (
		<button
			onClick={handleCopy}
			className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[#2D8A5E] transition-colors"
			title={t("copyEmail")}
		>
			{copied ? <Check className="h-3 w-3 text-[#2D8A5E]" /> : <Copy className="h-3 w-3" />}
			{copied ? t("copied") : null}
		</button>
	)
}

function ContactCard({ contact }: { contact: TenderContact }) {
	const t = useTranslations("tenderDetail.contacts")

	return (
		<div className="rounded-lg border border-gray-100 p-4 hover:border-gray-200 transition-colors">
			<div className="flex items-start gap-3">
				<div className="h-10 w-10 rounded-full bg-[#2D8A5E]/10 flex items-center justify-center shrink-0">
					<User className="h-5 w-5 text-[#2D8A5E]" />
				</div>
				<div className="flex-1 min-w-0">
					<p className="font-semibold text-gray-900 capitalize text-sm">{contact.name.toLowerCase()}</p>
					{contact.role && (
						<p className="text-xs text-muted-foreground mt-0.5">{contact.role}</p>
					)}
					{contact.organization && (
						<p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
							<Building2 className="h-3 w-3" />
							{contact.organization}
						</p>
					)}

					{/* Email section */}
					<div className="mt-3 space-y-1.5">
						{contact.email && (
							<div className="flex items-center gap-2">
								<Mail className="h-3.5 w-3.5 text-[#2D8A5E]" />
								<a href={`mailto:${contact.email}`} className="text-sm text-[#2D8A5E] hover:underline font-medium">
									{contact.email}
								</a>
								<CopyButton text={contact.email} />
							</div>
						)}
						{contact.org_contact_email && (
							<div className="flex items-center gap-2">
								<Building2 className="h-3.5 w-3.5 text-blue-500" />
								<a href={`mailto:${contact.org_contact_email}`} className="text-sm text-blue-600 hover:underline">
									{contact.org_contact_email}
								</a>
								<Badge variant="outline" className="text-[10px] px-1.5 py-0 text-blue-600 border-blue-200">
									{t("orgEmail")}
								</Badge>
								<CopyButton text={contact.org_contact_email} />
							</div>
						)}
						{!contact.email && !contact.org_contact_email && (
							<p className="text-xs text-muted-foreground italic">{t("noEmail")}</p>
						)}
					</div>

					{/* Phone */}
					{contact.phone && (
						<div className="flex items-center gap-2 mt-1.5">
							<Phone className="h-3.5 w-3.5 text-gray-400" />
							<a href={`tel:${contact.phone}`} className="text-sm text-gray-700 hover:underline">
								{contact.phone}
							</a>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default function TenderDetailPage() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const t = useTranslations("tenderDetail")
	const { locale } = useLocaleStore()

	const [tender, setTender] = useState<Tender | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [activeTab, setActiveTab] = useState<Tab>("details")

	useEffect(() => {
		if (!id) return
		setIsLoading(true)
		api
			.get<TenderDetailResponse>(`/tenders/${id}`)
			.then((res) => setTender(res.data))
			.catch(() => setError(t("notFound")))
			.finally(() => setIsLoading(false))
	}, [id])

	if (isLoading) {
		return (
			<DashboardLayout>
				<div className="flex h-64 items-center justify-center">
					<LoadingSpinner size="lg" />
				</div>
			</DashboardLayout>
		)
	}

	if (error || !tender) {
		return (
			<DashboardLayout>
				<div className="text-center py-12">
					<p className="text-muted-foreground mb-4">{error || t("notFound")}</p>
					<Button variant="outline" onClick={() => navigate("/")}>
						<ArrowLeft className="h-4 w-4 mr-2" />
						{t("back")}
					</Button>
				</div>
			</DashboardLayout>
		)
	}

	const deadlineDiff = tender.submission_deadline
		? Math.ceil((new Date(tender.submission_deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
		: null

	const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
		{ key: "details", label: t("tabs.details"), icon: <FileText className="h-4 w-4" /> },
		{ key: "documents", label: t("tabs.documents"), icon: <Download className="h-4 w-4" /> },
		{ key: "contacts", label: t("tabs.contacts"), icon: <Users className="h-4 w-4" /> },
	]

	return (
		<DashboardLayout>
			{/* Back button */}
			<div>
				<Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-muted-foreground hover:text-foreground -ml-2">
					<ArrowLeft className="h-4 w-4 mr-2" />
					{t("back")}
				</Button>
			</div>

			{/* Title section */}
			<div className="bg-gradient-to-r from-[#2d5c3f]/5 via-[#3d7a4f]/5 to-[#4a9960]/5 border border-[#2d5c3f]/10 rounded-xl p-6">
				<div className="flex items-start justify-between gap-4 flex-wrap">
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-2 mb-2 flex-wrap">
							<StatusBadge status={tender.status} />
							<Badge variant="outline" className="text-xs font-mono">
								<Tag className="h-3 w-3 mr-1" />
								{tender.expedient}
							</Badge>
							{tender.contract_type && (
								<Badge variant="outline" className="text-xs">
									<Briefcase className="h-3 w-3 mr-1" />
									{tender.contract_type}
								</Badge>
							)}
							{tender.procedure && (
								<Badge variant="outline" className="text-xs">
									<Gavel className="h-3 w-3 mr-1" />
									{tender.procedure}
								</Badge>
							)}
						</div>
						<h1 className="text-xl font-bold text-gray-900 leading-snug capitalize">{tender.name}</h1>
						{tender.contracting_org_name && (
							<p className="mt-2 text-sm text-muted-foreground flex items-center gap-1.5">
								<Building2 className="h-3.5 w-3.5 shrink-0" />
								{tender.contracting_org_name}
							</p>
						)}
					</div>

					{deadlineDiff !== null && deadlineDiff >= 0 && (
						<div className="text-center bg-white rounded-lg border border-[#2d5c3f]/20 px-4 py-3 shrink-0">
							<p className="text-2xl font-bold text-[#2D8A5E]">{deadlineDiff}</p>
							<p className="text-xs text-muted-foreground">{t("daysRemaining")}</p>
						</div>
					)}
				</div>
			</div>

			{/* Tab navigation */}
			<div className="border-b border-gray-200">
				<nav className="flex gap-1">
					{tabs.map((tab) => (
						<button
							key={tab.key}
							onClick={() => setActiveTab(tab.key)}
							className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
								activeTab === tab.key
									? "border-[#2D8A5E] text-[#2D8A5E]"
									: "border-transparent text-muted-foreground hover:text-gray-700 hover:border-gray-300"
							}`}
						>
							{tab.icon}
							{tab.label}
						</button>
					))}
				</nav>
			</div>

			{/* Tab content */}
			{activeTab === "details" && (
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					{/* Left: details */}
					<div className="lg:col-span-2 space-y-4">
						<h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t("dataTitle")}</h2>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
							<InfoCard
								icon={<span className="text-base">€</span>}
								label={t("budget")}
								value={formatCurrency(tender.budget_no_taxes)}
							/>
							{tender.award_amount != null && (
								<InfoCard
									icon={<span className="text-base">€</span>}
									label={t("awardAmount")}
									value={formatCurrency(tender.award_amount)}
								/>
							)}
							<InfoCard
								icon={<Calendar className="h-4 w-4" />}
								label={t("deadline")}
								value={
									<div>
										<div>{formatDate(tender.submission_deadline)}</div>
										<div className="text-xs font-normal text-muted-foreground">{formatDateRelative(tender.submission_deadline, locale)}</div>
									</div>
								}
							/>
							<InfoCard
								icon={<Calendar className="h-4 w-4" />}
								label={t("publishedAt")}
								value={formatDate(tender.published_at)}
							/>
							{tender.location && (
								<InfoCard
									icon={<MapPin className="h-4 w-4" />}
									label={t("location")}
									value={tender.location}
								/>
							)}
							<InfoCard
								icon={<FileText className="h-4 w-4" />}
								label={t("numLots")}
								value={tender.num_lots > 0 ? t("lots", { count: tender.num_lots }) : t("noLots")}
							/>
						</div>

						{/* Description */}
						{tender.description && (
							<div className="bg-gray-50 rounded-lg border border-gray-100 p-4">
								<p className="text-xs text-muted-foreground font-medium mb-1">{t("description")}</p>
								<p className="text-sm text-gray-700 leading-relaxed">{tender.description}</p>
							</div>
						)}

						{/* Bidder info */}
						{tender.bidder_org_name && (
							<InfoCard
								icon={<Building2 className="h-4 w-4" />}
								label={t("awardedTo")}
								value={tender.bidder_org_name}
							/>
						)}

						{/* CPV codes */}
						{tender.cpv_codes && tender.cpv_codes.length > 0 && (
							<div className="flex items-center gap-2 flex-wrap">
								<span className="text-xs text-muted-foreground">CPV:</span>
								{tender.cpv_codes.map((code) => (
									<Badge key={code} variant="outline" className="text-xs font-mono">{code}</Badge>
								))}
							</div>
						)}

						{/* Open in Tendios */}
						<div className="pt-2">
							<a
								href={`https://bid.tendios.com/tender/${tender.id}/summary`}
								target="_blank"
								rel="noopener noreferrer"
							>
								<Button variant="outline" className="gap-2 text-[#2D8A5E] border-[#2D8A5E]/30 hover:bg-[#2D8A5E]/5">
									<ExternalLink className="h-4 w-4" />
									{t("openInTendios")}
								</Button>
							</a>
						</div>
					</div>

					{/* Right: AI Analysis */}
					<div className="space-y-4">
						<h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t("aiAnalysis")}</h2>
						<RelevanceCard score={tender.ai_relevance_score} reason={tender.ai_relevance_reason} />

						{/* Pipeline progress */}
						<div className="mt-4 p-3 bg-gray-50 rounded-lg border">
							<h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("pipeline.title")}</h3>
							<div className="space-y-1.5">
								{[
									{ done: tender.detail_fetched, icon: FileSearch, label: t("pipeline.detail") },
									{ done: tender.ai_relevance_score != null, icon: Cpu, label: t("pipeline.aiScore") },
									{ done: tender.documents_scraped, icon: FileText, label: t("pipeline.documents") },
									{ done: tender.contacts_extracted, icon: Users, label: t("pipeline.contacts") },
								].map((step, i) => (
									<div key={i} className={`flex items-center gap-2 text-xs ${step.done ? "text-green-600" : "text-muted-foreground"}`}>
										{step.done ? <CheckCircle className="h-3.5 w-3.5" /> : <CircleDashed className="h-3.5 w-3.5" />}
										<step.icon className="h-3.5 w-3.5" />
										<span>{step.label}</span>
									</div>
								))}
							</div>
						</div>

						{/* Sync info */}
						<div className="text-xs text-muted-foreground text-right mt-2">
							{t("syncedAt")}: {formatDate(tender.synced_at)}
						</div>
					</div>
				</div>
			)}

			{activeTab === "documents" && id && <DocumentsTab tenderId={id} />}
			{activeTab === "contacts" && id && <ContactsTab tenderId={id} />}
		</DashboardLayout>
	)
}
